import { getSupabase } from '$db/remote/supabase';
import { getActiveUserId } from '$core/identity/active-user';
import type {
  Friend,
  FriendRequest,
  FriendRequestWithUser,
  Group,
  GroupMember,
  PublicUser,
  SocialResult
} from './types';

/**
 * Repo layer para amigos y grupos (M4.8d).
 * Toda la comunicación con Supabase pasa por aquí. La UI y los stores
 * NUNCA llaman a Supabase directamente.
 */

interface FriendRequestRow {
  id: string;
  sender_id: string;
  receiver_id: string;
  status: 'pending' | 'accepted' | 'declined' | 'cancelled';
  created_at: string;
  responded_at: string | null;
}

interface FriendshipRow {
  user_low_id: string;
  user_high_id: string;
  created_at: string;
}

interface GroupRow {
  id: string;
  owner_id: string;
  name: string;
  invite_code: string;
  created_at: string;
}

interface GroupMemberRow {
  group_id: string;
  user_id: string;
  role: 'owner' | 'member';
  joined_at: string;
}

interface LeaderboardRow {
  user_id: string;
  display_name: string;
  country: string;
  avatar_url: string | null;
}

function mapRequest(row: FriendRequestRow): FriendRequest {
  return {
    id: row.id,
    senderId: row.sender_id,
    receiverId: row.receiver_id,
    status: row.status,
    createdAt: row.created_at,
    respondedAt: row.responded_at
  };
}

function mapGroup(
  row: GroupRow,
  role: 'owner' | 'member',
  memberCount: number
): Group {
  return {
    id: row.id,
    ownerId: row.owner_id,
    name: row.name,
    inviteCode: row.invite_code,
    createdAt: row.created_at,
    role,
    memberCount
  };
}

function mapMember(row: GroupMemberRow): GroupMember {
  return {
    userId: row.user_id,
    role: row.role,
    joinedAt: row.joined_at,
    profile: null
  };
}

function mapProfile(row: LeaderboardRow): PublicUser {
  return {
    userId: row.user_id,
    displayName: row.display_name,
    country: row.country,
    avatarUrl: row.avatar_url
  };
}

/**
 * Lookup de perfiles públicos desde leaderboard_entries (scope global).
 * Devuelve un Map para acceso O(1). Los user_ids sin fila se omiten.
 */
async function fetchProfilesByIds(
  ids: string[]
): Promise<Map<string, PublicUser>> {
  const result = new Map<string, PublicUser>();
  if (ids.length === 0) return result;

  const client = getSupabase();
  if (!client) return result;

  const { data, error } = await client
    .from('leaderboard_entries')
    .select('user_id, display_name, country, avatar_url')
    .eq('scope_id', 'global')
    .in('user_id', ids);

  if (error || !data) {
    if (error && import.meta.env.DEV) {
      console.warn('[StudyQuantifier] fetchProfilesByIds failed:', error.message);
    }
    return result;
  }

  for (const row of data as LeaderboardRow[]) {
    result.set(row.user_id, mapProfile(row));
  }
  return result;
}

// ---------------------------------------------------------------------------
// Búsqueda y solicitudes
// ---------------------------------------------------------------------------

export async function searchUsers(query: string): Promise<PublicUser[]> {
  const trimmed = query.trim();
  if (trimmed.length === 0) return [];
  const client = getSupabase();
  if (!client) return [];

  const { data, error } = await client.rpc('search_user_by_display_name', {
    p_name: trimmed
  });

  if (error || !data) {
    if (error && import.meta.env.DEV) {
      console.warn('[StudyQuantifier] searchUsers failed:', error.message);
    }
    return [];
  }

  return (data as LeaderboardRow[]).map(mapProfile);
}

export async function sendFriendRequest(
  receiverId: string
): Promise<SocialResult<{ id: string }>> {
  const client = getSupabase();
  if (!client) return { ok: false, error: 'no_supabase' };

  const senderId = getActiveUserId();
  const { data, error } = await client
    .from('friend_requests')
    .insert({
      sender_id: senderId,
      receiver_id: receiverId,
      status: 'pending'
    })
    .select('id')
    .single();

  if (error || !data) {
    if (import.meta.env.DEV) {
      console.warn('[StudyQuantifier] sendFriendRequest failed:', error?.message);
    }
    return { ok: false, error: error?.message ?? 'insert_failed' };
  }

  return { ok: true, data: { id: data.id } };
}

export async function cancelFriendRequest(
  requestId: string
): Promise<SocialResult<void>> {
  const client = getSupabase();
  if (!client) return { ok: false, error: 'no_supabase' };

  const { error } = await client
    .from('friend_requests')
    .delete()
    .eq('id', requestId);

  if (error) {
    return { ok: false, error: error.message };
  }
  return { ok: true, data: undefined };
}

export async function acceptFriendRequest(
  requestId: string
): Promise<SocialResult<void>> {
  return respondToRequest(requestId, 'accepted');
}

export async function declineFriendRequest(
  requestId: string
): Promise<SocialResult<void>> {
  return respondToRequest(requestId, 'declined');
}

async function respondToRequest(
  requestId: string,
  status: 'accepted' | 'declined'
): Promise<SocialResult<void>> {
  const client = getSupabase();
  if (!client) return { ok: false, error: 'no_supabase' };

  const { error } = await client
    .from('friend_requests')
    .update({ status, responded_at: new Date().toISOString() })
    .eq('id', requestId);

  if (error) {
    return { ok: false, error: error.message };
  }
  return { ok: true, data: undefined };
}

// ---------------------------------------------------------------------------
// Listas
// ---------------------------------------------------------------------------

async function listRequests(
  direction: 'incoming' | 'outgoing'
): Promise<FriendRequestWithUser[]> {
  const client = getSupabase();
  if (!client) return [];

  const myId = getActiveUserId();
  const column = direction === 'incoming' ? 'receiver_id' : 'sender_id';

  const { data, error } = await client
    .from('friend_requests')
    .select('id, sender_id, receiver_id, status, created_at, responded_at')
    .eq(column, myId)
    .eq('status', 'pending')
    .order('created_at', { ascending: false });

  if (error || !data) {
    if (error && import.meta.env.DEV) {
      console.warn(`[StudyQuantifier] list${direction}Requests failed:`, error.message);
    }
    return [];
  }

  const rows = data as FriendRequestRow[];
  const otherIds = rows.map((r) =>
    direction === 'incoming' ? r.sender_id : r.receiver_id
  );
  const profiles = await fetchProfilesByIds(otherIds);

  return rows.map((r) => {
    const otherId = direction === 'incoming' ? r.sender_id : r.receiver_id;
    return {
      ...mapRequest(r),
      otherUser: profiles.get(otherId) ?? null
    };
  });
}

export function listIncomingRequests(): Promise<FriendRequestWithUser[]> {
  return listRequests('incoming');
}

export function listOutgoingRequests(): Promise<FriendRequestWithUser[]> {
  return listRequests('outgoing');
}

export async function listFriends(): Promise<Friend[]> {
  const client = getSupabase();
  if (!client) return [];

  const myId = getActiveUserId();
  const { data, error } = await client
    .from('friendships')
    .select('user_low_id, user_high_id, created_at')
    .or(`user_low_id.eq.${myId},user_high_id.eq.${myId}`)
    .order('created_at', { ascending: false });

  if (error || !data) {
    if (error && import.meta.env.DEV) {
      console.warn('[StudyQuantifier] listFriends failed:', error.message);
    }
    return [];
  }

  const rows = data as FriendshipRow[];
  const otherIds = rows.map((r) =>
    r.user_low_id === myId ? r.user_high_id : r.user_low_id
  );
  const profiles = await fetchProfilesByIds(otherIds);

  return rows.map((r) => {
    const otherId = r.user_low_id === myId ? r.user_high_id : r.user_low_id;
    return {
      userId: otherId,
      createdAt: r.created_at,
      profile: profiles.get(otherId) ?? null
    };
  });
}

export async function removeFriend(friendId: string): Promise<SocialResult<void>> {
  const client = getSupabase();
  if (!client) return { ok: false, error: 'no_supabase' };

  const myId = getActiveUserId();
  const low = myId < friendId ? myId : friendId;
  const high = myId < friendId ? friendId : myId;

  const { error } = await client
    .from('friendships')
    .delete()
    .eq('user_low_id', low)
    .eq('user_high_id', high);

  if (error) return { ok: false, error: error.message };
  return { ok: true, data: undefined };
}

// ---------------------------------------------------------------------------
// Grupos
// ---------------------------------------------------------------------------

interface GroupJoinRow {
  group_id: string;
  role: 'owner' | 'member';
  joined_at: string;
  leaderboard_groups: GroupRow;
}

export async function listMyGroups(): Promise<Group[]> {
  const client = getSupabase();
  if (!client) return [];

  const myId = getActiveUserId();
  const { data, error } = await client
    .from('leaderboard_group_members')
    .select(
      'group_id, role, joined_at, leaderboard_groups!inner(id, owner_id, name, invite_code, created_at)'
    )
    .eq('user_id', myId);

  if (error || !data) {
    if (error && import.meta.env.DEV) {
      console.warn('[StudyQuantifier] listMyGroups failed:', error.message);
    }
    return [];
  }

  const rows = data as unknown as GroupJoinRow[];
  const groupIds = rows.map((r) => r.group_id);

  const counts = new Map<string, number>();
  if (groupIds.length > 0) {
    const { data: memberRows } = await client
      .from('leaderboard_group_members')
      .select('group_id')
      .in('group_id', groupIds);
    for (const m of (memberRows ?? []) as { group_id: string }[]) {
      counts.set(m.group_id, (counts.get(m.group_id) ?? 0) + 1);
    }
  }

  return rows.map((r) =>
    mapGroup(r.leaderboard_groups, r.role, counts.get(r.group_id) ?? 1)
  );
}

export async function createGroup(
  name: string
): Promise<SocialResult<{ groupId: string; inviteCode: string }>> {
  const client = getSupabase();
  if (!client) return { ok: false, error: 'no_supabase' };

  const { data, error } = await client.rpc('create_group', { p_name: name });

  if (error || !data) {
    if (import.meta.env.DEV) {
      console.warn('[StudyQuantifier] createGroup failed:', error?.message);
    }
    return { ok: false, error: error?.message ?? 'rpc_failed' };
  }

  const payload = data as {
    ok: boolean;
    group_id?: string;
    invite_code?: string;
    error?: string;
  };

  if (!payload.ok || !payload.group_id || !payload.invite_code) {
    return { ok: false, error: payload.error ?? 'unknown' };
  }

  return {
    ok: true,
    data: { groupId: payload.group_id, inviteCode: payload.invite_code }
  };
}

export async function joinGroupByCode(
  code: string
): Promise<SocialResult<{ groupId: string }>> {
  const client = getSupabase();
  if (!client) return { ok: false, error: 'no_supabase' };

  const { data, error } = await client.rpc('join_group_by_code', {
    p_code: code
  });

  if (error || !data) {
    return { ok: false, error: error?.message ?? 'rpc_failed' };
  }

  const payload = data as { ok: boolean; group_id?: string; error?: string };
  if (!payload.ok || !payload.group_id) {
    return { ok: false, error: payload.error ?? 'unknown' };
  }

  return { ok: true, data: { groupId: payload.group_id } };
}

export async function leaveGroup(
  groupId: string
): Promise<SocialResult<void>> {
  const client = getSupabase();
  if (!client) return { ok: false, error: 'no_supabase' };

  const { data, error } = await client.rpc('leave_group', {
    p_group_id: groupId
  });

  if (error || !data) {
    return { ok: false, error: error?.message ?? 'rpc_failed' };
  }

  const payload = data as { ok: boolean; error?: string };
  if (!payload.ok) return { ok: false, error: payload.error ?? 'unknown' };

  return { ok: true, data: undefined };
}

export async function deleteGroup(
  groupId: string
): Promise<SocialResult<void>> {
  const client = getSupabase();
  if (!client) return { ok: false, error: 'no_supabase' };

  const { error } = await client
    .from('leaderboard_groups')
    .delete()
    .eq('id', groupId);

  if (error) return { ok: false, error: error.message };
  return { ok: true, data: undefined };
}

export async function listGroupMembers(
  groupId: string
): Promise<GroupMember[]> {
  const client = getSupabase();
  if (!client) return [];

  const { data, error } = await client
    .from('leaderboard_group_members')
    .select('group_id, user_id, role, joined_at')
    .eq('group_id', groupId)
    .order('joined_at', { ascending: true });

  if (error || !data) {
    if (error && import.meta.env.DEV) {
      console.warn('[StudyQuantifier] listGroupMembers failed:', error.message);
    }
    return [];
  }

  const rows = data as GroupMemberRow[];
  const profiles = await fetchProfilesByIds(rows.map((r) => r.user_id));

  return rows.map((r) => ({
    ...mapMember(r),
    profile: profiles.get(r.user_id) ?? null
  }));
}